import { Spinner } from '@chakra-ui/spinner';
import { FC, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useCollapse } from 'react-collapsed';
import { useTranslation } from 'react-i18next';
import { ReactComponent as IconDone } from '../../assets/done.svg';
import { ReactComponent as IconEdit } from '../../assets/edit.svg';
import styles from './CollapsableBlock.module.scss';

interface CollapsableBlockProps {
  title: string;
  selectedLabels?: (string | number | undefined)[];
  editable: boolean;
  active: boolean;
  onEdit: () => void;
  noScroll?: boolean;
}

const CollapsableBlock: FC<PropsWithChildren<CollapsableBlockProps>> = ({
  children,
  title,
  selectedLabels,
  editable,
  active,
  onEdit,
  noScroll,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(!!children && active);
  const { getCollapseProps, getToggleProps } = useCollapse({ isExpanded });
  const ref = useRef<HTMLDivElement>(null);
  const [doneAfterLastCollapse, setDoneAfterLastCollapse] = useState<boolean>(false);

  useEffect(() => {
    if (!isExpanded)
      setDoneAfterLastCollapse(!!selectedLabels?.every((label) => label !== null && label !== undefined));
  }, [isExpanded, selectedLabels]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    timeout = setTimeout(() => {
      isExpanded &&
        !noScroll &&
        ref.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
    }, 750);

    return () => {
      clearTimeout(timeout);
    };
  }, [isExpanded, noScroll]);

  useEffect(() => {
    children && setIsExpanded(active);
    !active && setIsExpanded(active);
  }, [active, children]);

  return (
    <div ref={ref} className={styles.collapsableBlock}>
      <div className={styles.container}>
        {isExpanded ? (
          <div className={`${styles.expandedTitle} ${doneAfterLastCollapse ? styles.done : ''}`}>{title}</div>
        ) : selectedLabels?.every((label) => label !== null && label !== undefined) ? (
          <div className={styles.labelsContainer}>
            <div>
              <div className={styles.doneTitleContainer}>
                <IconDone />
                <div className={styles.title}>{title}</div>
              </div>

              <div className={styles.selectedLabels}>
                {selectedLabels.map((label, index) => (
                  <div className={styles.label} key={index}>
                    {label}
                  </div>
                ))}
              </div>
            </div>

            {editable && (
              <button
                className={styles.editContainer}
                {...getToggleProps({
                  onClick: () => {
                    onEdit?.();
                  },
                })}>
                <IconEdit />
                <span>{t('edit')}</span>
              </button>
            )}
          </div>
        ) : (
          <div className={styles.titleContainer}>
            <div className={`${styles.title} ${editable ? '' : styles.inactive}`}>{title}</div>
            {editable && children && !active && (
              <div
                className={styles.editContainer}
                {...getToggleProps({
                  onClick: () => {
                    onEdit?.();
                  },
                })}>
                <IconEdit />
                <span>{t('edit')}</span>
              </div>
            )}
            {active && !children && <Spinner color="#228285" />}
          </div>
        )}
        <div {...getCollapseProps()}>
          <div className={`${styles.children} ${isExpanded ? '' : styles.collapsed}`}>{children}</div>
        </div>
      </div>
    </div>
  );
};

export default CollapsableBlock;
